import { supabase } from '@/supabase';
import { useAuthStore } from '#imports';
import type { Location } from '~/types';

export const useLocationStore = defineStore('locationStore', () => {
    const { active_emp, currentLocationId, isAdmin, isDemo, profile } = storeToRefs(useAuthStore());
    const _isPending = ref<boolean>(false);
    const locations = ref<Array<any>>([]);
    const location = ref<any>({});
    const locations_cnt = ref<number>(0);

    const isPending = computed(() => { return _isPending.value });
    
    const params = ref<any>({
        isAsc: true,
        orderBy: 'name',
        page: 1,
        perPage: Number.MAX_SAFE_INTEGER,
        search: ''
    });

    const startIndex = computed(() => { return (params.value.page - 1) * params.value.perPage; });
    const endIndex = computed(() => { return startIndex.value + params.value.perPage - 1 });

    function $reset(): void {
        locations.value = [];
        location.value = {};
        locations_cnt.value = 0;
        _isPending.value = false;
        params.value = {
            isAsc: true,
            orderBy: 'name',
            page: 1,
            perPage: Number.MAX_SAFE_INTEGER,
            search: ''
        }
    }

    async function fetchLocations(): Promise<string> {
        if (!isAdmin.value) {
            locations.value = [active_emp.value ? active_emp.value.location : null];
            locations_cnt.value = 1;
            return '';
        }

        if (_isPending.value || !profile.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';

        let query = supabase
            .from(isDemo.value ? 'location_demo' : 'location')
            .select('id, name, start, end, addr1, addr2, city, state, zip, code, owner_id, req_window, max_req_per_day', { count: 'exact', head: false });

        //if (!isSuperAdmin.value) query = query.eq('owner_id', profile.value.account_id);
        query = query.eq('owner_id', profile.value.account_id);

        query = query.order(params.value.orderBy, { ascending: params.value.isAsc })
            .range(startIndex.value, endIndex.value);

        const { data, count, error } = await query;

        if (error) {
            console.log(error)
            msg = error.message as string;
        }
        else if (data) {
            console.log(data)
            locations.value = data;
            locations_cnt.value = count || 0;
        }
        
        _isPending.value = false;
        return msg;
    }

    async function updateLocation(location: any): Promise<string> {
        if (isDemo.value) {
            return 'The setup section cannot be modified in demo mode.';
        }
        
        if (_isPending.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';
        
        const { error } = await supabase
            .from(isDemo.value ? 'location_demo' : 'location')
            .update([
                {
                    name: location.name,
                    start: new Date(location.start).toISOString(),
                    end: new Date(location.end).toISOString(),
                    addr1: location.addr1,
                    addr2: location.addr2,
                    city: location.city,
                    state: location.state,
                    zip: location.zip,
                    req_window: location.req_window,
                    max_req_per_day: location.max_req_per_day
                }
            ])
            .eq('id', location.id)
            .select();

        if (error) {
            msg = error.message as string;
        }

        _isPending.value = false;
        return msg;
    }

    async function checkLocationCode(code: string): Promise<string> {
        if (isDemo.value) {
            return 'Link codes cannot be redeemed in demo mode.';
        }
        if (_isPending.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';

        const { data, error } = await supabase
            .from(isDemo.value ? 'location_demo' : 'location')
            .select('*')
            .is('owner_id', null)
            .eq('code', code.toUpperCase());

        if (error) {
            msg = error.message as string;
        }

        _isPending.value = false;
        return msg;
    }

    async function redeemLocationCode(code: string, item: Location): Promise<string> {
        if (isDemo.value) {
            return 'Link codes cannot be redeemed in demo mode.';
        }
        if (_isPending.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';

        const { data, error } = await supabase
            .from('location')
            .update({
                owner_id: profile.value.account_id,
                created_at: new Date().toISOString(),
                name: item.name,
                start: new Date(item.start).toISOString(),
                end: new Date(item.end).toISOString(),
                addr1: item.addr1,
                addr2: item.addr2,
                city: item.city,
                state: item.state,
                zip: item.zip,
                req_window: item.req_window,
                max_req_per_day: item.max_req_per_day
            })
            .is('owner_id', null)
            .eq('code', code.toUpperCase())
            .select();

        if (error) {
            msg = error.message as string;
        }
        else if (data) {
            if (!data[0]) {
                msg = 'Invalid Link Code';
            }
            else {
                location.value = data[0];
                if (location.value) {
                    currentLocationId.value = location.value.id;
                }
            }
        }

        _isPending.value = false;
        return msg;
    }

    return {
        locations,
        location,
        locations_cnt,
        isPending,
        params,
        checkLocationCode,
        fetchLocations,
        redeemLocationCode,
        updateLocation,
        $reset
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useLocationStore, import.meta.hot))
}